import React from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import '../assets/css/components/tyto-flowchart.css'

class TytoFlowchart extends React.Component {
  static propTypes = {
    nodes: PropTypes.arrayOf(PropTypes.shape({
      id:       PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name:     PropTypes.string.isRequired,
      imgLink:  PropTypes.string.isRequired
    })),
    edges: PropTypes.arrayOf(PropTypes.shape({
      fromId:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      toId:       PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      isCorrect:  PropTypes.bool.isRequired
    }))
  }

  static circleRadius = 5

  constructor(props) {
    super(props)
    this.canvas = React.createRef()
  }

  initCanvas = ()=> {
    const canvasEl = this.canvas.current

    // Resize the canvas to match its container
    canvasEl.width  = canvasEl.offsetWidth;
    canvasEl.height = canvasEl.offsetHeight

    return canvasEl.getContext('2d')
  }

  edgesWithIndex = ()=> {
    const { nodes, edges } = this.props
    let edgesWithIndex = edges

    const getNodeIndex = id => _.findIndex(nodes, node => node.id == id)

    return _.each(edgesWithIndex, edge => {
      edge.fromIndex = getNodeIndex(edge.fromId)
      edge.toIndex = getNodeIndex(edge.toId)
    })
  }

  drawNode = (x, y, context, node) => {
    // init setup
    const nodeRadius = 50
    context.beginPath()

    // Draw the node perimeter
    context.arc(x, y, nodeRadius, 0, Math.PI * 2)

    // Fill the circle with a color
    context.fillStyle = '#172D3B'
    context.fill()

    // Load the node image on top of the circle
    let newImage = new Image()
    newImage.onload = function() {
      context.drawImage(newImage, x-nodeRadius, y-nodeRadius, nodeRadius*2, nodeRadius*2)
    }
    newImage.src = node.imgLink

    // Draw the text with a color
    context.fillStyle = '#1D6A8B'
    context.font = 'bold 14px Montserrat'
    context.textAlign = 'center'
    context.fillText(node.name, x, y + nodeRadius + 20)
  }

  edgeThetaRelativeToXAxis = (originX, originY, destX, destY) => {
    // Theta represents the value that the arrow must be rotated in order to align with the drawn edge and in
    // the proper direction.
    //
    // Given and x and y in any of the 4 quadrants, we can solve for theta with the following formula:
    // theta = 2arcsin( y / x + sqrt(x^2 + y^2) )
    //
    // However, we are given two sets of (x,y) pairs. To calculate x and y for the above formula, we can use the
    // translated x,y values calculated above. They are translated such that the line begins at the origin, ie,
    // (originX,originY) = (0,0) instead of being drawn solely in quadrant 1.
    const transY = destY - originY
    const transX = destX - originX
    //console.log('transx', transX)
    //console.log('transy', transY)
    return 2 * (Math.atan(transY / (transX + Math.sqrt(Math.pow(transX,2) + Math.pow(transY,2)))) || Math.PI/2) + (Math.PI / 2)
  }

  drawArrow = (originX, originY, destX, destY, context, fillColor) => {
    context.beginPath()
    const arrowEdgeLength = 20 // Arbitrary

    // Calculate where the midpoint of the line is
    const midpointX = (originX + destX) / 2
    const midpointY = (originY + destY) / 2

    // The pi/2 term is needed because the arrow is drawn vertically, ie it's already at a 90 degree angle
    // relative to the x-axis.
    const theta = this.edgeThetaRelativeToXAxis(originX, originY, destX, destY)

    // The translate and rotate commands don't work on individual elements, they apply to the entire canvas.
    // In order to rotate an individual element, the current state must be saved and then restored later.
    // Google solutions for translate/rotate on a single element for more information.
    context.save()
    // The arrow will be drawn directly on the midpoint of the line, so the canvas needs to be translated to this point.
    context.translate(midpointX, midpointY)
    context.rotate(theta)

    // Trace the arrow pointing upwards
    context.moveTo(0,0)
    context.lineTo(-arrowEdgeLength / 2, arrowEdgeLength)
    context.lineTo(0, arrowEdgeLength * 0.7)
    context.lineTo(arrowEdgeLength / 2, arrowEdgeLength)
    context.lineTo(0, 0)

    // Set arrow styles and draw it
    context.lineWidth = 2
    context.strokeStyle = '#000D28'
    context.fillStyle = fillColor
    context.stroke()
    context.fill()

    // Restore the canvas state that was saved earlier
    context.restore()
  }

  drawEdge = (originX, originY, destX, destY, context, edge) => {
    const theta = this.edgeThetaRelativeToXAxis(originX, originY, destX, destY)
    const offsetRadius = 12 //arbitrary

    //console.log("Before: (" + originX + "," + originY + "), (" + destX + "," + destY + ")")
    //console.log("radis: ", offsetRadius)
    //console.log("theta: ", theta)

    originX = offsetRadius * Math.cos(theta + Math.PI / 4) + originX
    originY = offsetRadius * Math.sin(theta + Math.PI / 4) + originY
    destX = offsetRadius * Math.cos(theta - Math.PI / 4) + destX
    destY = offsetRadius * Math.sin(theta - Math.PI / 4) + destY

    //console.log("After: (" + originX + "," + originY + "), (" + destX + "," + destY + ")")

    context.beginPath()
    context.moveTo(originX, originY)
    context.lineTo(destX, destY)
    context.lineWidth = 8

    const strokeColor = edge.isCorrect ? '#01D946' : '#D58F93'
    context.strokeStyle = strokeColor

    context.stroke()
    context.closePath()

    this.drawArrow(originX, originY, destX, destY, context, strokeColor)
  }

  getCoordinate = (index, axis) => {
    // How many nodes are in the flowchart
    const nodeCount = this.props.nodes.length
    // Radians between two adjacent nodes
    const theta = (Math.PI * 2) / parseFloat(nodeCount)
    // Canvas width
    const width = this.canvas.current.width
    // Canvas Height
    const height = this.canvas.current.height
    // Radius of the circle the nodes are being placed onto. Arbitrary
    const displayRadius = 150
    // Offset to account for the padding between the canvas edge and the display circle
    const cartesianOffset = axis === 'x' ? ((width / 2) - displayRadius) : ((height - 2 * displayRadius) / 2)
    const trigOp = axis === 'x' ? Math.cos : Math.sin

    // Based on the formula for polar->cartesian conversion with two offsets:
    // 1) the cartesianOffset used to create padding between the circle and the sides of the canvas to center it, and
    // 2) the displayRadius which effectively moves the circle from being central to the cartesian quadrants to
    //    existing directly on the boundaries of quadrant 1
    return displayRadius * trigOp(index * theta) + cartesianOffset + displayRadius
  }
  getYCoordinate = (radius, index, theta, cartesianOffset) => radius * Math.sin(index * theta) + cartesianOffset + radius

  drawFlowchart = ()=> {
    const ctx = this.initCanvas()
    const { nodes, edges } = this.props
    const edgesWithIndex = this.edgesWithIndex()

    for(var i = 0; i < edges.length; i++) {
      this.drawEdge(
        this.getCoordinate(edgesWithIndex[i].fromIndex, 'x'),
        this.getCoordinate(edgesWithIndex[i].fromIndex, 'y'),
        this.getCoordinate(edgesWithIndex[i].toIndex, 'x'),
        this.getCoordinate(edgesWithIndex[i].toIndex, 'y'),
        ctx,
        edgesWithIndex[i]
      )
    }

    for(var i = 0; i < nodes.length; i++) {
      this.drawNode(this.getCoordinate(i, 'x'), this.getCoordinate(i, 'y'), ctx, nodes[i])
    }
  }

  componentDidMount() {
    this.drawFlowchart()
  }

  render() {
    return(
      <div className='tyto-flowchart'>
        <canvas ref={this.canvas}/>
      </div>
    )
  }
}

export default TytoFlowchart
